import React from 'react';
import { Alert, Box, List, ListItem, ListItemText, Paper, Stack, Typography } from '@mui/material';
import type { TitrationReading } from './Notebook';

export interface PHPoint {
  baseVolumeCm3: number;
  pH: number;
}

interface PHCurvePlotProps {
  curve: PHPoint[];
  currentVolumeCm3: number;
  equivalenceVolumeCm3: number;
  transitionRange?: { min: number; max: number };
}

export const PHCurvePlot: React.FC<PHCurvePlotProps> = ({ curve, currentVolumeCm3, equivalenceVolumeCm3, transitionRange }) => {
  const width = 560;
  const height = 190;
  const padding = { left: 40, right: 12, top: 14, bottom: 32 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const maxVolume = Math.max(curve[curve.length - 1]?.baseVolumeCm3 || 50, 1);
  const x = (volume: number) => padding.left + (volume / maxVolume) * plotWidth;
  const y = (pH: number) => padding.top + ((14 - pH) / 14) * plotHeight;
  const path = curve.map((point, index) => `${index === 0 ? 'M' : 'L'}${x(point.baseVolumeCm3)},${y(point.pH)}`).join(' ');
  const current = curve.reduce((nearest, point) => Math.abs(point.baseVolumeCm3 - currentVolumeCm3) < Math.abs(nearest.baseVolumeCm3 - currentVolumeCm3) ? point : nearest, curve[0] || { baseVolumeCm3: 0, pH: 7 });

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>pH curve · end point vs equivalence</Typography>
      {curve.length > 1 ? (
        <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="pH against titrant volume curve">
          {[0, 2, 4, 6, 8, 10, 12, 14].map((tick) => (
            <g key={tick}>
              <line x1={padding.left} x2={width - padding.right} y1={y(tick)} y2={y(tick)} stroke="#d9e0e5" strokeWidth="1" />
              <text x={padding.left - 8} y={y(tick) + 4} textAnchor="end" fontSize="10" fill="#53636c">{tick}</text>
            </g>
          ))}
          {transitionRange && <rect x={padding.left} y={y(transitionRange.max)} width={plotWidth} height={Math.max(2, y(transitionRange.min) - y(transitionRange.max))} fill="#ecbb46" opacity="0.18" />}
          <line x1={x(equivalenceVolumeCm3)} x2={x(equivalenceVolumeCm3)} y1={padding.top} y2={height - padding.bottom} stroke="#bf4f3d" strokeDasharray="4 4" />
          <line x1={x(currentVolumeCm3)} x2={x(currentVolumeCm3)} y1={padding.top} y2={height - padding.bottom} stroke="#177e89" strokeDasharray="3 3" />
          <path d={path} fill="none" stroke="#155c79" strokeWidth="2.4" />
          <circle cx={x(current.baseVolumeCm3)} cy={y(current.pH)} r="4" fill="#177e89" />
          <text x={padding.left + plotWidth / 2} y={height - 5} textAnchor="middle" fontSize="10" fill="#34464e">Titrant added (cm³)</text>
          <text x="11" y={padding.top + plotHeight / 2} textAnchor="middle" fontSize="10" fill="#34464e" transform={`rotate(-90 11 ${padding.top + plotHeight / 2})`}>pH</text>
        </svg>
      ) : (
        <Typography variant="caption" color="text.secondary">pH curve loads when the chemistry service is available.</Typography>
      )}
      <Stack direction="row" spacing={1.5} flexWrap="wrap">
        <Typography variant="caption"><Box component="span" sx={{ color: '#bf4f3d', fontWeight: 700 }}>— —</Box> Equivalence point: {equivalenceVolumeCm3.toFixed(2)} cm³</Typography>
        <Typography variant="caption"><Box component="span" sx={{ color: '#177e89', fontWeight: 700 }}>— —</Box> Current volume: {currentVolumeCm3.toFixed(2)} cm³</Typography>
        {transitionRange && <Typography variant="caption">Shaded band: indicator transition pH {transitionRange.min}–{transitionRange.max}</Typography>}
      </Stack>
    </Paper>
  );
};

export const IndicatorChoiceVisualiser: React.FC<{ suitable: boolean | null; message: string }> = ({ suitable, message }) => (
  <Alert severity={suitable === null ? 'info' : suitable ? 'success' : 'warning'}>
    <strong>Indicator check:</strong> {message || 'Choose an indicator to compare its transition range with the expected pH change.'}
  </Alert>
);

export const ConcordanceTable: React.FC<{ readings: TitrationReading[]; meanTitreCm3: number | null; needsRepeat: boolean }> = ({ readings, meanTitreCm3, needsRepeat }) => (
  <Paper variant="outlined" sx={{ p: 1.5 }}>
    <Typography variant="subtitle2" fontWeight={700}>Titre concordance</Typography>
    <Typography variant="body2" color="text.secondary">
      Compare accurate titres only; repeat until at least two agree within ±0.10 cm³. The rough titre is excluded.
    </Typography>
    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
      {readings.map((reading, index) => (
        <Box key={reading.id} component="span" sx={{ px: 1, py: 0.5, border: '1px solid', borderColor: reading.runType === 'rough' ? 'divider' : reading.concordant ? 'success.main' : 'warning.main', borderRadius: 1, fontSize: 12 }}>
          {reading.runType === 'rough' ? 'Rough' : `Accurate ${index}`}: {reading.titreCm3.toFixed(2)} cm³{reading.runType === 'rough' ? ' · excluded' : reading.concordant === null ? ' · pending' : reading.concordant ? ' · concordant' : ' · repeat'}
        </Box>
      ))}
    </Stack>
    {meanTitreCm3 !== null && <Typography variant="body2" sx={{ mt: 1 }}>Mean concordant titre: <strong>{meanTitreCm3.toFixed(2)} cm³</strong></Typography>}
    {needsRepeat && <Alert severity="warning" sx={{ mt: 1 }}>Continue with accurate titrations; the concordance requirement is not yet met.</Alert>}
  </Paper>
);

export const StoichiometryWalkthrough: React.FC<{ analyteMoles: number | null; titrantMoles: number | null; ratioText: string; resultText: string }> = ({ analyteMoles, titrantMoles, ratioText, resultText }) => (
  <Paper variant="outlined" sx={{ p: 1.5 }}>
    <Typography variant="subtitle2" fontWeight={700}>Stoichiometry walkthrough</Typography>
    <List dense disablePadding>
      <ListItem sx={{ px: 0 }}><ListItemText primary="1. Convert the titre from cm³ to dm³." secondary="Volume (dm³) = volume (cm³) ÷ 1000" /></ListItem>
      <ListItem sx={{ px: 0 }}><ListItemText primary="2. Calculate titrant amount." secondary={titrantMoles === null ? 'n = c × V' : `n(titrant) = ${titrantMoles.toPrecision(4)} mol`} /></ListItem>
      <ListItem sx={{ px: 0 }}><ListItemText primary={`3. Apply the balanced-equation ratio: ${ratioText}.`} secondary={analyteMoles === null ? 'Use the coefficients in the balanced equation.' : `n(analyte) = ${analyteMoles.toPrecision(4)} mol`} /></ListItem>
      <ListItem sx={{ px: 0 }}><ListItemText primary="4. Calculate the concentration of the measured aliquot." secondary={resultText || 'c = n ÷ V (with V in dm³)'} /></ListItem>
    </List>
  </Paper>
);